import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, FileText, X, Eye } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  headline: "", bio: "", category: "consulting", expertise: [], years_experience: 25,
  hourly_rate: 1500, location: "", languages: [], company_history: "",
};

export default function ProfileEditPage() {
  const { user, switchRole } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [expInput, setExpInput] = useState("");
  const [langInput, setLangInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadDocs = async () => {
    try { const { data } = await api.get("/uploads/me"); setDocs(data); }
    catch { /* ignore */ }
  };

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); loadDocs(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/professionals/me");
        if (data && data.headline) setForm({ ...EMPTY, ...data });
      } catch { /* ignore */ }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addChip = (key, val, setVal) => {
    if (!val.trim()) return;
    setForm((f) => ({ ...f, [key]: [...(f[key] || []), val.trim()] }));
    setVal("");
  };
  const removeChip = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (user.role !== "professional") await switchRole("professional");
      await api.put("/professionals/me", {
        ...form,
        years_experience: Number(form.years_experience),
        hourly_rate: Number(form.hourly_rate),
      });
      toast.success("Profile submitted. Awaiting verification.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setUploading(true);
    try {
      if (user.role !== "professional") await switchRole("professional");
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/uploads/document?purpose=verification", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      await loadDocs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/uploads/${docId}`);
      toast.success("Removed");
      loadDocs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
      <div className="label-eyebrow text-[#2DD4BF] mb-3">Your profile</div>
      <h1 className="font-display text-4xl text-[#E8ECF3] mb-8">Tell the world what you've built.</h1>

      <form onSubmit={save} className="space-y-6 bg-[#0F1B33] border border-[#142542] rounded-3xl p-8">
        <div>
          <Label>Headline</Label>
          <Input required data-testid="profile-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)}
                 placeholder="Former CFO | 32 years in Corporate Finance" className="mt-2 h-12" />
        </div>
        <div>
          <Label>Bio</Label>
          <Textarea required rows={5} data-testid="profile-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)}
                    placeholder="Share your journey, what you love helping with…" className="mt-2" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-2 h-12" data-testid="profile-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Years of experience</Label>
            <Input required type="number" min={1} max={60} data-testid="profile-years"
                   value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)}
                   className="mt-2 h-12" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Hourly rate (₹) — <span className="text-[#2DD4BF]">You decide</span></Label>
            <Input required type="number" min={100} data-testid="profile-rate"
                   value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)}
                   className="mt-2 h-12" />
            <div className="mt-2 text-xs text-[#94A3B8]">
              You earn <span className="text-[#2DD4BF] font-semibold">₹{Math.round((Number(form.hourly_rate) || 0) * 0.88).toLocaleString("en-IN")}/hr</span> after 12% platform fee.
              Popular ranges: ₹800 · ₹1,500 · ₹2,500 · ₹5,000
            </div>
          </div>
          <div>
            <Label>Location</Label>
            <Input required data-testid="profile-location" value={form.location} onChange={(e) => set("location", e.target.value)}
                   placeholder="Bengaluru, India" className="mt-2 h-12" />
          </div>
        </div>

        <div>
          <Label>Expertise tags</Label>
          <div className="flex gap-2 mt-2">
            <Input value={expInput} onChange={(e) => setExpInput(e.target.value)} placeholder="e.g., Fundraising" className="h-11" data-testid="profile-expertise-input" />
            <Button type="button" variant="outline" onClick={() => addChip("expertise", expInput, setExpInput)} data-testid="profile-add-expertise">Add</Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {(form.expertise || []).map((e, i) => (
              <button type="button" key={i} onClick={() => removeChip("expertise", i)}
                      className="bg-[#0F1B33] hover:bg-[#2DD4BF] hover:text-white transition-colors text-[#E8ECF3] rounded-full px-3 py-1 text-sm">
                {e} ×
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Languages</Label>
          <div className="flex gap-2 mt-2">
            <Input value={langInput} onChange={(e) => setLangInput(e.target.value)} placeholder="e.g., English" className="h-11" data-testid="profile-lang-input" />
            <Button type="button" variant="outline" onClick={() => addChip("languages", langInput, setLangInput)}>Add</Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {(form.languages || []).map((e, i) => (
              <button type="button" key={i} onClick={() => removeChip("languages", i)}
                      className="bg-[#0F1B33] hover:bg-[#2DD4BF] hover:text-white transition-colors text-[#E8ECF3] rounded-full px-3 py-1 text-sm">
                {e} ×
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Companies you've worked with (optional)</Label>
          <Textarea rows={3} value={form.company_history || ""} onChange={(e) => set("company_history", e.target.value)}
                    className="mt-2" placeholder="Infosys, HDFC, ITC…" />
        </div>

        <div className="border-t border-[#142542] pt-6">
          <Label className="text-[#E8ECF3] text-base">Verification Documents</Label>
          <p className="text-xs text-[#94A3B8] mt-1 mb-4">
            Upload your ID proof, resume, or professional certificates. Admin will review these before marking you as Verified.
            PDF / JPG / PNG · Max 5MB each.
          </p>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden"
                 onChange={handleFile} data-testid="doc-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-full border border-dashed border-[#2DD4BF]/50 rounded-2xl p-6 hover:bg-[#2DD4BF]/5 text-[#94A3B8] flex flex-col items-center gap-2 transition-colors"
                  data-testid="doc-upload-btn">
            <Upload className="h-6 w-6 text-[#2DD4BF]" />
            <div className="text-sm">{uploading ? "Uploading…" : "Click to upload verification document"}</div>
          </button>

          {docs.length > 0 && (
            <div className="mt-4 space-y-2">
              {docs.map((d) => (
                <div key={d.doc_id} className="flex items-center gap-3 bg-[#0A1223] border border-[#142542] rounded-xl p-3" data-testid={`doc-item-${d.doc_id}`}>
                  <FileText className="h-4 w-4 text-[#2DD4BF]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#E8ECF3] truncate">{d.original_filename}</div>
                    <div className="text-xs text-[#94A3B8]">{(d.size / 1024).toFixed(1)} KB · {d.purpose}</div>
                  </div>
                  <a href={`${API}/uploads/${d.doc_id}/view`} target="_blank" rel="noreferrer" className="text-[#2DD4BF] hover:text-white p-1" data-testid={`doc-view-${d.doc_id}`}>
                    <Eye className="h-4 w-4" />
                  </a>
                  <button type="button" onClick={() => removeDoc(d.doc_id)} className="text-[#94A3B8] hover:text-red-400 p-1" data-testid={`doc-remove-${d.doc_id}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#2DD4BF] hover:bg-[#14B8A6] text-[#05070C]" data-testid="profile-save">
          {busy ? "Saving…" : "Save & submit for verification"}
        </Button>
        <div className="text-xs text-[#94A3B8] text-center">Admin will review your profile and mark you as verified.</div>
      </form>
    </div>
  );
}
